/**
 * Created by Yoyo Fang on 09/30/19.
 * Description:
 *  implement interface of dashboard tiles for tam dashboard grid tile
 * ------ maintenance history ------
 * 05/13/2020 Yoyo add ability wrap cell content by each grid column
 * 9/7/2021 Simon Zhao introduced entity service to use the short name of the focused entity as part of the exported excel's name.
 */
import { TamDashboardGridTileBaseConfiguration } from '../tam-dashboard-tile.base.config';
import { Subject, Subscription, Observable } from 'rxjs';
import { TamDashboardActions, TamDashboardCustomComponentsActions, TamDashboardEvents } from '../tam-dashboard.model';
import { DashboardService } from '../../../pages/dashboard/dashboard.service';
import { ElementRef } from '@angular/core';
import { filter, take, map, delay } from 'rxjs/operators';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { ContextMenuConfig } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LayoutService } from '../../../services/layout.service';
import * as _ from 'lodash';
import { DashboardActionTypes } from '../../../redux/actions/dashboard.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../../redux';
import { DashboardTileService } from '../dashboard-tile.service';
import { CellClickedEvent } from 'ag-grid-community';
import { defaultShortName } from '../../../tamalelibs/models/tile-column.model';
import { getFocusStateSelector } from '../../../redux/reducers/dashboard.reducer';
import { StoreQuerierService } from './../../../services/store-querier.service';

export interface ExpandGroup {
    key: string;
    id: string;
    parent: ExpandGroup;
}

const BLANK_VALUE = '(Blanks)';

export class TamDashboardGridTileConfiguration implements TamDashboardGridTileBaseConfiguration {

    // public data: TamDashboardTileData[];
    displayDataLen = 0;
    id: string;
    initialized: boolean;
    name: string;
    readonly: boolean;
    seriesClickEventType: string;
    actionSubject$: Subject<any>;
    feedbackSubject$: Subject<any>;
    expandAll: boolean;
    expandGroups: ExpandGroup[] = [];
    data: any = [];
    otherGridOptions = {
        enableRowGroup: true,
        domLayout: 'normal',
        rowGroupPanelShow: 'always',
        sortable: true,
        resizable: true,
        suppressMenu: false,
        suppressMovable: false,
        maxWidth: 600,
        minWidth: 50
    };
    subscriptions: Subscription[] = [];
    groupRowRendererParams = {
        // suppressCount: true,
        tileConfig: {},
        action$: new Subject()
    };

    private _agGrid: ElementRef;
    private _columnDefs: any = [];
    private _columnIdsWithOrder: Array<string> = [];
    private _columnSorts = [];
    private _fullGridWidth = 0;
    private _focusedEntityIds: any;
    private _gridApi;
    private _gridColumnApi;
    private _tileConfig: any;
    private _gridFilters: any = [];
    private _gridColumns: any = [];
    private _widget: any;
    private _allowedAggFuncs = ['sum', 'min', 'max', 'count', 'avg', 'none'];
    private _gridReadySubject = new Subject();

    private _oldPositionForScroll = 0;
    private _endScrollHandle;

    constructor(
        private _dashboardService: DashboardService,
        private _deviceService: DeviceDetectorService | null,
        private _utils: UtilsService,
        private _dashboardTileService: DashboardTileService,
        private _layoutService?: LayoutService,
        private _store?: Store<AppState>,
        private _storeQuerier?: StoreQuerierService
    ) {
        this.initialized = false;
    }

    setWidget(widget) {
        this._widget = widget;
    }

    destroy() {
        this.subscriptions.forEach((subscription: Subscription) => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
    }

    init(id: string, readonly: boolean, name: string, actionSubject$: Subject<any>, feedbackSubject$: Subject<any>, otherGridOptions?): boolean {
        if (this.initialized === false) {
            this.id = id;
            this.readonly = readonly;
            this.name = name;
            this.actionSubject$ = actionSubject$;
            this.feedbackSubject$ = feedbackSubject$;
            this.otherGridOptions = Object.assign({}, this.otherGridOptions, otherGridOptions);
            this.subscriptions.push(
                this.actionSubject$.pipe(
                    filter((action: any) => action && action.id === this.id)
                ).subscribe((action) => this._actionHandler(action)),
                this.feedbackSubject$.pipe(
                    filter((event: any) => event && event.id === this.id)
                ).subscribe((event) => {
                    this._eventHandler(event);
                }),
            );
            this._loadLayout({ widget: this._widget });
            this.initialized = true;
            this._toggleLoadingStatus(this._widget.id, true);
            return true;
        } else {
            return false;
        }
    }

    getContextMenuItems(): any {
        if (this.otherGridOptions.domLayout === 'print') {
            return [];
        }
        return (param) => this.readonly ? this._dashboardService.initMenu(param, this.id, this.feedbackSubject$, this.actionSubject$, this._tileConfig) : [];
    }

    onCellClicked(): any {
        return (event: CellClickedEvent) => {
            if (this.readonly) {
                if (event.column && event.data) {
                    const colId = event.column.getColId();
                    const cellAttribute = event.data[colId + '_attribute'];
                    if (cellAttribute && cellAttribute.threadId) {
                        event.event['slideSheet'] = true;
                        const entityIds = event.data[`${defaultShortName.guid}_attribute`].entityId;
                        this._dashboardService.openThreadDetailInSlideSheet(cellAttribute.threadId, this.id, this.feedbackSubject$, this.actionSubject$, this._tileConfig, entityIds);
                    }
                }
            }
        };
    }

    onFilterChanged() {
        return () => {
            if (this._gridApi) {
                this._ChangePrintStoreData(this._gridApi);

                this._setTotalCount();
                // for aggFunc
                this._setBottomGridData();
            } else {
                setTimeout(() => {
                    this.onFilterChanged();
                }, 500);
            }
        };
    }

    onColumnVisible() {
        return (event) => {
            this.setColumnsToFit(event.api);
            this._setBottomColumnVisible(event);
            this._updateGroupRowByColumns(event.column);
        };
    }

    onColumnMoved() {
        return (event) => {
            const tempArray: Array<string> = event.columnApi.getColumnState().map(item => item.colId);
            if (!this._utils.isEqual(tempArray, this._columnIdsWithOrder)) {
                this._columnIdsWithOrder = tempArray;
                this.feedbackSubject$.next({
                    type: TamDashboardEvents.ON_COLUMN_ORDER_CHANGED,
                    id: this.id,
                    payload: {
                        columnIds: this._columnIdsWithOrder,
                    }
                });
                this._updateGroupRowByColumns(event.column);
            }
        };
    }

    onColumnPinned() {
        return (event) => {
            const columns = event.columnApi.getColumnState();
            const leftColumn = columns.filter(column => column.pinned === 'left');
            const rightColumn = columns.filter(column => column.pinned === 'right');
            const noPinColumn = columns.filter(column => !column.pinned);
            const tempColumns = [].concat(leftColumn, noPinColumn, rightColumn);
            const tempArray: Array<string> = tempColumns.map(item => item.colId);
            if (!this._utils.isEqual(tempArray, this._columnIdsWithOrder)) {
                this._columnIdsWithOrder = tempArray;
                this.feedbackSubject$.next({
                    type: TamDashboardEvents.ON_COLUMN_ORDER_CHANGED,
                    id: this.id,
                    payload: {
                        columnIds: this._columnIdsWithOrder,
                    }
                });
                this._updateGroupRowByColumns(event.column);
            }
        };
    }

    onColumnResized() {
        return (event) => {
            if (event.finished) {
                this._resetRowHeights();
                if (event.source === 'uiColumnDragged') {
                    this.feedbackSubject$.next({
                        id: this.id,
                        type: TamDashboardEvents.RESIZE_COLUMN_WIDTH
                    });
                }
                if (event.source !== 'api') {
                    this._updateGroupRowByColumns(event.column);
                }
            }
        };
    }

    onRowGroupOpened() {
        return (event) => {
            this.expandAll = false;
            const node: ExpandGroup = this._getParentNode(event.node);
            const index = this.expandGroups.findIndex(item => item.id === node.id);
            if (event.node.expanded) {
                // node.setExpanded(true) trigger the function
                if (index < 0) {
                    this.expandGroups.push(node);
                }
            } else {
                if (index >= 0) {
                    this.expandGroups.splice(index, 1);
                }
            }
        };
    }

    onColumnRowGroupChanged() {
        return (e) => {
            this.expandAll = false;
            this.expandGroups = [];
            // Don't need to reset group column Comparator after upgrading. comment it out for now
            // this._setGridColumnComparator(e);
            this._resetBottomGridColumnWidth();
        };
    }

    onExpandOrCollapseAll() {
        return (event) => {
            this.expandAll = event.source === 'expandAll';
        };
    }

    getMainMenuItems() {
        return (params) => {
            const menuItems = [];
            const itemsToExclude = ['separator', 'autoSizeAll', 'resetColumns']; //  autoSizeThis
            params.defaultItems.forEach((item) => {
                if (itemsToExclude.indexOf(item) < 0) {
                    menuItems.push(item);
                }
            });
            const aggFunc = params.column.aggFunc;
            // group column not has columnSetting
            if (params.column.colDef && params.column.colDef.gridRefData && params.column.colDef.gridRefData.columnSetting) {
                const columnType = params.column.colDef.gridRefData.columnSetting.dataType;
                if (this._dashboardService.isNumberType(columnType)) {
                    const aggMenuItems = [];
                    this._allowedAggFuncs.forEach(item => {
                        if (!aggFunc && item === 'none') {
                            aggMenuItems.push({
                                name: item,
                                icon: '<span class="ag-icon ag-icon-tick" />',
                                action: () => {
                                    this._customColumnAggFuncs(item, params.column);
                                }
                            });
                        } else if (item === aggFunc) {
                            aggMenuItems.push({
                                name: item,
                                icon: '<span class="ag-icon ag-icon-tick" />',
                                action: () => {
                                    this._customColumnAggFuncs(item, params.column);
                                }
                            });
                        } else {
                            aggMenuItems.push({
                                name: item,
                                action: () => {
                                    this._customColumnAggFuncs(item, params.column);
                                }
                            });
                        }
                    });
                    menuItems.splice(1, 0, {
                        name: 'Aggregation',
                        icon: '<span class="ag-icon ag-icon-aggregation" />',
                        subMenu: aggMenuItems
                    });
                }
            }
            // per TAM-29453 renaming the 'Autosize This Column
            menuItems.push({
                name: 'Wrap Cell Content',
                action: () => {
                    params.column.autoHeight = !params.column.colDef.autoHeight;
                    this._setGridColumn({
                        payload: this._columnDefs.map(def =>
                            def.colId === params.column.colId ?
                                Object.assign({}, def, {
                                    autoHeight: params.column.autoHeight
                                })
                                : def)
                    });
                    setTimeout(() => {
                        // have to wait white-space settings get rendered by ag grid
                        this._resetRowHeights();
                    });
                },
                checked: params.column.colDef.autoHeight
            });
            return menuItems;
        };
    }

    getExcelStyles() {
        const styles = this._dashboardService.formatExcelStyle();
        styles.push({
            id: 'font_size10',
            font: {
                size: 10,
                fontName: 'Arial',
            }
        });
        return styles;
    }

    isPrintingFromServer(): boolean {
        return this._dashboardService.dashboardPrintFromServer;
    }

    initContextMenu(): ContextMenuConfig {
        let contextMenuConfig;
        if (this._deviceService) {
            contextMenuConfig = new ContextMenuConfig();
            contextMenuConfig.stateful = false;
            contextMenuConfig.showItemIcon = false;
            const isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
            contextMenuConfig.items = [
                { id: 'export', text: 'Export to Excel', disabled: isIPAD },
                { id: 'layout', text: 'Load Default Layout' },
                { id: 'autosize', text: 'Autosize All Columns' }
            ];
            contextMenuConfig.events$.subscribe(event => {
                // if not preventDefault, download file will open a new window
                event.payload.event.preventDefault();
                if (event.payload.id === 'export') {
                    // if cell font-color or backgroound-color not exit exportStyles, should update exportStyles
                    this.actionSubject$.next({
                        id: this.id,
                        type: TamDashboardActions.UPDATE_EXPORT_STYLES
                    });
                } else if (event.payload.id === 'layout') {
                    this.feedbackSubject$.next({
                        type: TamDashboardEvents.LOAD_DEFAULT_LAYOUT,
                        id: this.id
                    });
                } else if (event.payload.id === 'autosize') {
                    this.setColumnsToFit(this._gridApi, true);
                }
            });
        }
        return contextMenuConfig;
    }

    onGridReady(agGrid: ElementRef, params: any) {
        this._agGrid = agGrid;
        this._gridApi = params.api;
        this._gridColumnApi = params.columnApi;
        // if print preview need render all data, need to wait for the grid render finished
        this._gridReadySubject.next();
    }

    onBodyScroll(e) {
        if (e.direction === 'horizontal') {
            clearTimeout(this._endScrollHandle);
            this._endScrollHandle = setTimeout(() => {
                this.groupRowRendererParams.action$.next({
                    type: TamDashboardCustomComponentsActions.UPDATE_COLUMN_WIDTH_BY_SCROLL,
                    payload: e.left - this._oldPositionForScroll
                });
                this._oldPositionForScroll = e.left;
            }, 250);
        }
    }

    postSetData() {
        this._setGridSortColumns();
        this._resetRowHeights();
    }

    onExport() {
        const storeFocusValue = this._storeQuerier.queryBySelector(getFocusStateSelector);
        let fileName = this.name;
        if (storeFocusValue && storeFocusValue.shortName) {
            fileName = this.name + '_' + storeFocusValue.shortName;
        }
        this._dashboardService.exportToXLS(this._gridColumnApi, this._tileConfig.columns, this._gridApi, this.name, fileName);
    }

    /**
     * print preview use
     * @param data
     */
    setTileData(gsItem) {
        this.data = gsItem.data;
        this.actionSubject$.next({ id: this.id, type: TamDashboardActions.SET_TILE_DATA, payload: gsItem.data });
        this._tileConfig = gsItem.config;
        // for custom grid group render
        this.groupRowRendererParams.tileConfig = this._tileConfig;
        this.subscriptions.push(
            this._gridReadySubject.pipe(
                delay(250)
            ).subscribe(() => {
                this.feedbackSubject$.next({
                    type: TamDashboardEvents.TILE_AFTER_VIEW_INIT,
                    id: this.id
                });
                const isPrintLayout = this.otherGridOptions.domLayout === 'print';
                this.actionSubject$.next({
                    id: gsItem.id,
                    type: TamDashboardActions.SET_GRID_LAYOUT,
                    payload: {
                        ...gsItem,
                        tile: gsItem.config
                    },
                    resetColumns: isPrintLayout
                });
                // setSortModel need render column complete, so use setTimeout
                setTimeout(() => {
                    this._setGridSortColumns();
                    this._disabledColumnSort();
                    this._resetRowHeights();
                });
                this._setFilterSelect(this.id, gsItem.data);
            })
        );
    }

    /**
     * dashboard print for server - should reset grid column size order by tile config
     * dashboard print for tamaleweb - should not reset, only keep width dashboard-view page column width
     */
    resetColumnsToFit() {
        if (this.otherGridOptions.domLayout === 'print' && this._dashboardService.dashboardPrintFromServer) {
            this.setColumnsToFit(this._gridApi);
        }
    }

    /**
     * (1)init grid or column visible change, column width according to tile config
     * if tile config gridSetting.automaticSizing=true, call function by tile config
     * if tile config gridSetting.automaticSizing=false, call default behavior
     * (2) when click 'Autosize All Columns', call default behavior
     * default behavior - first auto size all columns, if total width < grid width call auto fit, min width = 50, max width = 600
     * @param {*} api
     * @param {boolean} [defaultBehavior=false]
     * @memberof TamDashboardGridTileConfiguration
     */
    setColumnsToFit(api, defaultBehavior = false) {
        if (this.otherGridOptions.domLayout === 'print' && !this._dashboardService.dashboardPrintFromServer) {
            return;
        }
        if (this._tileConfig) {
            const gridSetting = this._tileConfig.visualization.settings.gridSetting;
            if (gridSetting && gridSetting.automaticSizing && !defaultBehavior) {
                if (gridSetting.columnWithShowType === 'autoSize') {
                    this._gridColumnApi.autoSizeAllColumns();
                } else if (gridSetting.columnWithShowType === 'autoSizeSkipHeader') {
                    this._gridColumnApi.autoSizeAllColumns(true);
                } else if (gridSetting.columnWithShowType === 'sizeToFit') {
                    api.sizeColumnsToFit();
                    this._setBottomColumnWidthByTopGrid();
                }
            } else if (defaultBehavior) {
                this._gridColumnApi.autoSizeAllColumns();
                const fullWidth = this.sumColumnsWidth();
                if (fullWidth < this._fullGridWidth) {
                    api.sizeColumnsToFit();
                    this._setBottomColumnWidthByTopGrid();
                }
            }
        }
    }
    /**
     * same level group row comparator
     */
    customColumnSortComparator(nodeA, nodeB, columnType, rowGroupIndex) {
        // add nodeA, because click column menu will error
        const comparatorNodeA = this._getParentNodeByGroupIndex(nodeA, rowGroupIndex);
        const comparatorNodeB = this._getParentNodeByGroupIndex(nodeB, rowGroupIndex);
        if (comparatorNodeA && comparatorNodeA.level === rowGroupIndex && comparatorNodeB.level === rowGroupIndex) {
            const valueA = comparatorNodeA.key;
            const valueB = comparatorNodeB.key;
            if ((valueA === undefined && valueB === undefined) || (valueA === null && valueB === null)) {
                return 0;
            }
            if (valueA === undefined || valueA === null) {
                return -1;
            }
            if (valueB === undefined || valueB === null) {
                return 1;
            }
            if (this._dashboardService.isNumberType(columnType)) {
                if (!valueA && valueA !== 0 && (valueB || valueB === 0)) {
                    return -1;
                } else if (!valueB && valueB !== 0 && (valueA || valueA === 0)) {
                    return 1;
                }
                return parseInt(valueA, 10) - parseInt(valueB, 10);
            } else if (this._dashboardService.isDateType(columnType)) {
                if (!valueA && valueB) {
                    return -1;
                } else if (!valueB && valueA) {
                    return 1;
                }
                return new Date(valueA).getTime() - new Date(valueB).getTime();
            }
            return valueA.localeCompare(valueB);
        }
        return 0;
    }

    resetBottomState(bottomColumnDefs) {
        if (this._gridColumnApi) {
            const newColumnState = this._gridColumnApi.getColumnState();
            const columns = [];
            newColumnState.forEach(item => {
                const temp = bottomColumnDefs.find(column => column.colId === item.colId);
                if (temp) {
                    delete temp.rowGroupIndex;
                    temp.aggFunc = item.aggFunc;
                    temp.fromGrid = 'bottom';
                    temp.width = item.width;
                    columns.push(temp);
                } else {
                    delete item.rowGroupIndex;
                    item.fromGrid = 'bottom';
                    columns.push(item);
                }
            });
            return columns;
        }
    }

    /**
     * If the BA has special requirements, please customize the method here. You can also add other methods here, such as adding method 'xyz'
     * not custom count, because multi group column has problem
     * custom these function, to save return result for footer total value, if aggFunc=count, total value = grid displayDataLen
     */
    setAggFuncs() {
        return {
            sum: this._sumFunction.bind(this),
            min: this._minFunction.bind(this),
            max: this._maxFunction.bind(this),
            avg: this._avgFunction.bind(this),
            none: this._noneFunction.bind(this)
        };
    }

    sumColumnsWidth() {
        let fullWidth = 0;
        this._gridColumnApi.getColumns().forEach((column) => {
            if (column.visible) {
                fullWidth += column.actualWidth;
            }
        });
        return fullWidth;
    }

    private _ChangePrintStoreData(gridApi) {
        // change store data by filter
        if (gridApi) {
            const data = [];
            gridApi.clientSideRowModel.rowsToDisplay.forEach(item => {
                if (item.level === 0) {
                    if (item.data) {
                        data.push(item.data);
                    } else if (item.allLeafChildren) {
                        data.push(...item.allLeafChildren.map(_item => _item.data));
                    }
                }
            });

            if (this._store) {
                this._store.dispatch({
                    type: DashboardActionTypes.SAVE_TILE_DATA,
                    payload: {
                        id: this.id,
                        data: data,
                    }
                });
            }
        }
    }

    private _getArrayByObject(data) {
        if (Array.isArray(data)) {
            return data;
        } else if (Array.isArray(data.values)) {
            return data.values;
        }
    }

    private _sumFunction(values) {
        let result = 0;
        const data = this._getArrayByObject(values);
        data?.forEach((value) => {
            if (typeof value === 'number') {
                result += value;
            }
        });
        return result;
    }

    private _minFunction(values) {
        const data = this._getArrayByObject(values);
        if (Array.isArray(data)) {
            if (data.length === 0) {
                return null;
            }
            return Math.min(...data);
        }
    }

    private _maxFunction(values) {
        const data = this._getArrayByObject(values);
        if (Array.isArray(data)) {
            if (data.length === 0) {
                return null;
            }
            return Math.max(...data);
        }
    }

    private _avgFunction(values) {
        let sum = 0;
        let count = 0;
        const data = this._getArrayByObject(values);
        if (Array.isArray(data)) {
            data.forEach((value) => {
                const groupNode = value !== null && value !== undefined && typeof value === 'object';
                if (groupNode) {
                    sum += value.avg * value.count;
                    count += value.count;
                } else {
                    if (typeof value === 'number') {
                        sum += value;
                        count++;
                    }
                }
            });
        }
        let avg;
        if (count !== 0) {
            avg = sum / count;
        } else {
            avg = 0;
        }
        return avg;
    }

    private _noneFunction() {
        return '';
    }

    /**
     *  if top grid use toFit, must re-set column width to keep bottom grid column width equal top grid width
     */
    private _setBottomColumnWidthByTopGrid() {
        setTimeout(() => {
            const allColumns = this._gridColumnApi.getColumnState();
            allColumns.forEach(item => {
                // setColumnWidths - ag-grid version > 23, so use setColumnWidth
                this._gridColumnApi.setColumnWidth(item.colId, item.width);
            });
        });
    }

    private _setBottomGridData() {
        const columns = [];
        const allColumns = this._gridColumnApi.getColumns();
        allColumns.forEach((column) => {
            if (column.aggFunc) {
                columns.push({
                    aggFunc: column.aggFunc,
                    colId: column.colId,
                    data: []
                });
            }
        });
        this._gridApi.forEachNodeAfterFilter((node) => {
            if (!node.group) {
                columns.forEach(item => {
                    item.data.push(node.data[item.colId] ? (+node.data[item.colId]) : 0);
                });
            }
        });
        const bottomData = {};
        columns.forEach(item => {
            const aggFunc = item.aggFunc;
            if (aggFunc === 'avg') {
                bottomData[item.colId] = this._avgFunction(item.data);
            } else if (aggFunc === 'sum') {
                bottomData[item.colId] = this._sumFunction(item.data);
            } else if (aggFunc === 'min') {
                bottomData[item.colId] = this._minFunction(item.data);
            } else if (aggFunc === 'max') {
                bottomData[item.colId] = this._maxFunction(item.data);
            } else if (aggFunc === 'count') {
                bottomData[item.colId] = item.data.length;
            } else if (aggFunc === 'none') {
                bottomData[item.colId] = this._noneFunction();
            }
        });

        let data = [];
        if (Object.values(bottomData).every(item => item === '')) {
            data = [];
        } else {
            data = [bottomData];
        }
        this.actionSubject$.next({
            id: this.id,
            type: TamDashboardActions.SET_TILE_BOTTOM_GRID_DATA,
            payload: data
        });
        this._setBottomColumnWidthByTopGrid();
    }

    /**
     * Group / Un-Group check aggFunc
     * @param aggFunc
     * @param column
     */
    private _customColumnAggFuncs(aggFunc, column) {
        this._gridColumnApi.setColumnAggFunc(column, aggFunc);
        this._gridColumnApi.addValueColumn(column.colId);
        this._setBottomGridData();
    }

    private _getParentNodeByGroupIndex(node, rowGroupIndex) {
        let temp = node;
        while (temp && temp.level !== rowGroupIndex) {
            temp = temp.parent;
        }
        return temp;
    }

    /**
     *
     * @param params
     * set suppressAggFuncInHeader=true
     * Group / Un-Group
     */
    private _customHeaderValueGetter(params) {
        // params.location === 'columnDrop' | 'header'
        if (params.location === 'header' && params.column.aggFunc && params.column.aggFunc !== 'none') {
            return `${params.column.aggFunc}(${params.colDef.headerName})`;
        }
        return params.colDef.headerName;
    }

    private _resetBottomGridColumnWidth() {
        this.actionSubject$.next({
            id: this.id,
            type: TamDashboardActions.RESET_BOTTOM_GRID_COLUMN_WIDTH,
        });
    }

    private _setBottomColumnVisible(event) {
        if (event.column) {
            this.actionSubject$.next({
                id: this.id,
                type: TamDashboardActions.SET_BOTTOM_GRID_COLUMN_VISIBLE,
                payload: {
                    colId: event.column.colId,
                    visible: event.visible
                }
            });
        }
    }

    private _disabledColumnSort() {
        // set sort column need enabled sort, so need to disabled sort after setting sort column
        this.actionSubject$.next({
            id: this.id,
            type: TamDashboardActions.DISABLED_COLUMN_SORT
        });
    }

    private _setGridSortColumns() {
        if (this._gridColumnApi) {
            this._gridColumnApi.applyColumnState({ state: this._columnSorts });
        }
    }

    private _resetRowHeights() {
        if (this._gridApi) {
            this._gridApi.resetRowHeights();
            this._gridApi.redrawRows();
        }
    }

    /**
     * set grid layout - filter and expand group, total items
     */
    private _selectFilter(data) {
        if (!Array.isArray(data)) {
            return;
        }
        // Here setTimeout can't be removed.
        setTimeout(() => {
            if (this._gridApi && this._gridApi.getFilterInstance) {
                data.forEach(element => {
                    const agGridFilter = this._gridApi.getFilterInstance(element.field);
                    if (agGridFilter) {
                        const athleteFilterComponent = agGridFilter;
                        if (athleteFilterComponent) {
                            athleteFilterComponent.onRefreshFilter();
                            // Set the filter model to filter options
                            if (Array.isArray(element.value)) {
                                athleteFilterComponent.setModel({
                                    values: element.value,
                                    filterType: 'set',
                                    isIncludeUncheckedItems: true
                                });
                            }
                        }
                    }
                });
                if (this._gridApi) {
                    this._gridApi.onFilterChanged();
                    this._fullGridWidth = this._agGrid.nativeElement.clientWidth;
                    this.setColumnsToFit(this._gridApi);
                    this._resetBottomGridColumnWidth();
                    this._setGridGroupStatus();
                }
                this.feedbackSubject$.next({
                    type: TamDashboardEvents.TILE_LOADED_DATA,
                    id: this.id
                });
            }
        });
    }

    private _getGridLayout(subject$) {
        if (!this._gridColumnApi) {
            return;
        }
        const tempLayout = this._layout();
        const layout = this._dashboardService.getGridLayout({ ...tempLayout });
        subject$.next({
            type: 'GRID_LAYOUT',
            payload: { id: this.id, layout: layout }
        });

    }

    private _saveGridLayout(tileId) {
        const layout = this._layout();
        this._dashboardService.saveGridLayout(tileId, { ...layout });
    }

    private _setGridGroupStatus() {
        if (this.expandAll) {
            this._gridApi.expandAll();
        } else {
            if (this.expandGroups && this.expandGroups.length > 0) {
                this._gridApi.forEachNode((node) => {
                    const nodeKeys = this._getParentIds(node);
                    this.expandGroups.forEach(group => {
                        const groupKeys = this._getParentIds(group);
                        if (groupKeys === nodeKeys) {
                            node.setExpanded(true);
                        }
                    });
                });
            }
        }
    }

    private _setGridColumn(action) {
        this._columnDefs = action.payload;
        this._columnDefs.forEach(item => {
            const columnSetting = item.gridRefData.columnSetting;
            item.filter = 'customGridColumnFilter';
            item.filterParams = {
                columnSetting: columnSetting
            };
            // add cell renderer for file type columns
            if (this._dashboardService.isFileType(item.field)) {
                item.cellRenderer = 'cellRenderer';
                // used to change the value of parameters in custom group row inner renderer
                item.keyCreator = this._setGridFileColumnKeyCreator.bind(this);
            }
            const isNumberType = columnSetting.dataType === 'ratio' || columnSetting.dataType === 'percent' ||
                columnSetting.dataType === 'currency' || columnSetting.dataType === 'number';
            const isDateType = columnSetting.dataType === 'date';
            if (isNumberType) {
                item.enableValue = false;
                // default allowedAggFuncs = ['sum', 'min', 'max', 'count', 'avg', 'first', 'last']
                item.allowedAggFuncs = this._allowedAggFuncs;
                item.headerValueGetter = this._customHeaderValueGetter.bind(this);
            }
        });
        if (action.resetColumns) {
            const cols = this._columnDefs.map(column => column.colId);
            this._gridApi.setColumnDefs(cols);
        }
        const columns = this._columnDefs;
        columns.forEach((item) => {
            // group column default enableRowGroup = false;
            item.enableRowGroup = this.otherGridOptions['enableRowGroup'];

            if (item.rowGroupIndex || item.rowGroupIndex === 0) {
                const dataType = item.gridRefData.columnSetting.dataType;
                item.comparator = (valueA, valueB, nodeA, nodeB) => this.customColumnSortComparator(nodeA, nodeB, dataType, item.rowGroupIndex);
            }
        });
        this.actionSubject$.next({
            id: this.id,
            type: TamDashboardActions.SET_GRID_COLUMN_DEF,
            payload: columns
        });
    }

    private _setGridColumnComparator(e) {
        const columns = e.columns.map((item, i) => ({
            colId: item.colId,
            rowGroupIndex: i
        }));
        this.actionSubject$.next({
            id: this.id,
            type: TamDashboardActions.SET_COLUMN_SORT_COMPARATOR,
            payload: columns
        });
    }

    private _setGridFileColumnKeyCreator(params) {
        // handle the file object to show display name
        if (params.value && params.value.length > 0) {
            return params.value.map(item => item.displayName).join(', ');
        } else {
            return '';
        }
    }

    private _actionHandler(action) {
        switch (action.type) {
            case TamDashboardActions.LOAD_DATA: {
                if (action.payload && action.payload['tileConfig']) {
                    // this._widget = { id: action.id, tile: action.payload['tileConfig'] };
                    this._loadData(action.payload);
                }
                break;
            }
            case TamDashboardActions.SET_GRID_COLUMN: {
                this._setGridColumn(action);
                break;
            }
            case TamDashboardActions.SET_TILE_GRID_FILTER_SELECT: {
                this._selectFilter(action.payload);
                this.feedbackSubject$.next({ id: this.id, type: TamDashboardEvents.SET_DATA_FINISHED });
                break;
            }
            case TamDashboardActions.SAVE_TILE_GRID_LAYOUT: {
                this._saveGridLayout(action.payload);
                break;
            }
            case TamDashboardActions.GET_TILE_GRID_LAYOUT: {
                this._getGridLayout(action.subject$);
                break;
            }
            case TamDashboardActions.SET_TILE_CONFIG: {
                this._tileConfig = action.payload;
                // for custom grid group render
                this.groupRowRendererParams.tileConfig = this._tileConfig;
                break;
            }
            case TamDashboardActions.SET_GRID_COLUMN_SORT: {
                // if the grid re-set data, function setSortModel shuold called after data update
                this._columnSorts = action.payload;
                break;
            }
            case TamDashboardActions.SET_GRID_SORT_MODEL: {
                // if the grid only re-set layout, not refresh data, can called function setSortModel
                this._columnSorts = action.payload;
                this._gridColumnApi.applyColumnState({ state: this._columnSorts });
                break;
            }
            case TamDashboardActions.SET_EXPAND_OR_COLLAPSE_ALL: {
                this.expandAll = action.payload.expandAll;
                this.expandGroups = action.payload.expandGroups || [];
                break;
            }
            case TamDashboardActions.SET_GRID_LAYOUT: {
                this._setGridLayout(action.payload, action.resetColumns);
                break;
            }
            case TamDashboardActions.LOAD_GRID_LAYOUT: {
                this._loadLayout(action.payload);
                break;
            }
            case TamDashboardActions.UPDATE_GRID_ROW_DATA: {
                this._updateGridRowItems(action.payload);
                break;
            }
        }
    }

    private _eventHandler(event) {
        switch (event.type) {
            case TamDashboardEvents.LOAD_DEFAULT_LAYOUT: {
                this._loadDefaultLayout();
                break;
            }
        }
    }

    private _loadDefaultLayout() {
        this._toggleLoadingStatus(this._widget.id, true);
        this._loadGridLayout(this._widget, true).pipe(take(1)).subscribe((response) => {
            this._dashboardService.getGridAllData(this._widget.id, this.actionSubject$)
                .pipe(take(1)).subscribe((data) => {
                    this._setFilterSelect(this._widget.id, data['payload'].data);
                    this._toggleLoadingStatus(this._widget.id, false);
                    this._dashboardService.setColumnSortsOrExpandAll(this._widget.id, response, this,
                        TamDashboardActions.SET_GRID_SORT_MODEL);
                });
        });
    }

    private _loadData({ tileConfig, focusedEntityIds, useConfig = false, saveStore = false }) {
        this._focusedEntityIds = focusedEntityIds;
        this._dashboardTileService.gridLoadData(tileConfig, focusedEntityIds, useConfig).subscribe((data) => {
            this.data = data;
            if (saveStore && data) {
                this._ChangePrintStoreData(this._gridApi);
            }
            this.actionSubject$.next({ id: this.id, type: TamDashboardActions.SET_TILE_DATA, payload: data });
            this._setFilterSelect(this.id, data);
        });
    }

    private _loadGridLayout(dashboardItem: any, resetColumns = false, notGetLayoutFromServer = false) {
        let gridColumns = this._dashboardService.formatGridColumns(dashboardItem);
        if (notGetLayoutFromServer || !this._layoutService || !dashboardItem.tile.id) {
            const observable = new Observable(subscriber => {
                subscriber.next();
                subscriber.complete();
            });
            return observable.pipe(
                delay(250), // dashboard-print-preview-dialog need delay
                map(() => {
                    this._setGridLayout(dashboardItem, resetColumns);
                }),
            );
        } else {
            return this._layoutService.getTileLayout(dashboardItem.tile.id).pipe(
                take(1),
                map(response => {
                    if (response && Object.keys(response).length !== 0) {
                        let groupColIds = [];
                        if (response['groups']) {
                            groupColIds = response['groups'].split(',');
                        }
                        if (response['columns'] && response['columns'].length > 0) {
                            const _gridColumns = [];
                            response['columns'].forEach((item) => {
                                if (item.Name) { // web 1.0
                                    const index = gridColumns.findIndex(data => data.field === item.Name);
                                    if (index !== -1) {
                                        const column = gridColumns[index];
                                        column['hide'] = !!item.Hidden;
                                        if (item.Sec === 0) {
                                            column['pinned'] = 'left';
                                        }
                                        column['autoHeight'] = item.autoHeight;
                                        column['width'] = item.width;
                                        _gridColumns.push(column);
                                    }
                                } else if (item.name) { // web 2.0
                                    const index = gridColumns.findIndex(data => data.field === item.name);
                                    if (index !== -1) {
                                        const column = gridColumns[index];
                                        column.hide = !!item.hide;
                                        column.pinned = item.pinned;
                                        column.autoHeight = item.autoHeight;
                                        column.width = item.width;
                                        column.aggFunc = item.aggFunc;
                                        _gridColumns.push(column);
                                    }
                                }
                            });
                            gridColumns = _gridColumns;
                        }
                        gridColumns.forEach((element: any) => {
                            const groupIndex = groupColIds.indexOf(element.colId);
                            if (groupIndex > -1) {
                                element.rowGroupIndex = groupIndex;
                            }
                        });
                        let hadColumnValue = false;
                        this._gridColumns.forEach(item => {
                            if (item.tileId === dashboardItem.id) {
                                hadColumnValue = true;
                                item.columns = gridColumns;
                            }
                        });
                        if (!hadColumnValue) {
                            this._gridColumns.push({
                                tileId: dashboardItem.id,
                                columns: gridColumns
                            });
                        }
                        if (response['filters'] && Array.isArray(response['filters'])) {
                            this._resetGridFilter(dashboardItem.id, response['filters']);
                        }
                    } else {
                        const filterIndex = this._gridFilters.findIndex(item => item.tileId === dashboardItem.id);
                        if (filterIndex !== -1) {
                            this._gridFilters.splice(filterIndex, 1);
                        }
                        const columnIndex = this._gridColumns.findIndex(item => item.tileId === dashboardItem.id);
                        if (columnIndex !== -1) {
                            this._gridColumns.splice(columnIndex, 1);
                        }
                        let layout = {};
                        if (dashboardItem.tile && dashboardItem.tile.layout) {
                            layout = dashboardItem.tile.layout;
                        }
                        gridColumns = this._dashboardService.setGridColumnsLayout(layout, gridColumns);
                    }
                    this.actionSubject$.next({
                        id: dashboardItem.id,
                        type: TamDashboardActions.SET_GRID_COLUMN,
                        payload: gridColumns,
                        resetColumns: resetColumns
                    });
                    this._dashboardService.setColumnSortsOrExpandAll(dashboardItem.id, response, this);
                    return response;
                }),
            );
        }
    }

    private _resetGridFilter(tileId, filters) {
        let hadFilterValue = false;
        this._gridFilters.forEach(item => {
            if (item.tileId === tileId) {
                hadFilterValue = true;
                item.filter = _.cloneDeep(filters);
            }
        });
        if (!hadFilterValue) {
            this._gridFilters.push({
                tileId: tileId,
                filter: _.cloneDeep(filters)
            });
        }
    }

    /**
     * refresh tile, set grid layout
     * @param dashboardItem
     * @param resetColumns
     * set column, column order, column pin, column group
     * init filter data, sort data and expand group data
     */

    private _setGridLayout(dashboardItem, resetColumns = false) {
        let gridColumns = this._dashboardService.formatGridColumns(dashboardItem);
        const layout = dashboardItem.tile.layout;
        if (layout) {
            gridColumns = this._dashboardService.setGridColumnsLayout(layout, gridColumns);
        }
        let hadColumnValue = false;
        this._gridColumns.forEach(item => {
            if (item.tileId === dashboardItem.id) {
                hadColumnValue = true;
                item.columns = gridColumns;
            }
        });
        if (!hadColumnValue) {
            this._gridColumns.push({
                tileId: dashboardItem.id,
                columns: gridColumns
            });
        }
        if (layout && layout.filters && Array.isArray(layout.filters)) {
            this._resetGridFilter(dashboardItem.id, layout.filters);
        }
        this.actionSubject$.next({
            id: dashboardItem.id,
            type: TamDashboardActions.SET_GRID_COLUMN,
            payload: gridColumns,
            resetColumns: resetColumns
        });
        if (layout) {
            this._dashboardService.setColumnSortsOrExpandAll(dashboardItem.id, layout, this);
        }
    }

    private _setFilterSelect(id: string, columns: any) {
        const filters = this._dashboardService.setGridFilterSelect(id, columns, this._gridFilters, this._gridColumns);
        this.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_GRID_FILTER_SELECT,
            payload: filters,
        });
    }

    private _toggleLoadingStatus(id: string, isLoading: boolean) {
        this.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_LOADING_STATUS,
            payload: isLoading
        });
    }

    private _layout() {
        const columns = this._gridColumnApi.getColumnState();
        columns.forEach(column => {
            column['autoHeight'] = !!this._gridColumnApi.getColumn(column.colId)['colDef']['autoHeight'];
        });
        const filtersData = this._gridApi.getFilterModel();
        const filters = this._filterDataValue(filtersData);
        const columnState = this._gridColumnApi.getColumnState();
        const sorts = columnState
            .filter(column => column.sort)
            .map(column => ({ colId: column.colId, sort: column.sort }));

        // const sorts = this._gridApi.getSortModel();
        const expandAll = this.expandAll;
        const expandGroups = this.expandGroups;
        return { columns, filters, sorts, expandAll, expandGroups };
    }

    private _getParentNode(node) {
        const result: ExpandGroup = {
            key: null,
            parent: null,
            id: null
        };
        result.id = this._getParentIds(node);
        result.key = node.key;
        if (node.parent.key) {
            result.parent = this._getParentNode(node.parent);
        }
        return result;
    }

    private _getParentIds(node) {
        const ids = [node.key];
        if (node.parent && node.parent.key) {
            ids.push(this._getParentIds(node.parent));
        }
        return ids.join(',');
    }

    private _loadLayout({ widget, resetColumns = false, notGetLayoutFromServer = false }) {
        this._loadGridLayout(widget, resetColumns, notGetLayoutFromServer).pipe(
            take(1)
        ).subscribe((response) => {
            // add new tile in dashboard edit page
            if (!this._widget.tileId) {
                this._toggleLoadingStatus(this._widget.id, false);
            }

            this.feedbackSubject$.next({ id: this.id, type: TamDashboardEvents.SET_CONFIG_FINISHED });
        });
    }

    private _filterDataValue(filters) {
        const filterData = {};
        for (const item in filters) {
            if (filters.hasOwnProperty(item) && filters[item].filterType && filters[item].filterType === 'set' && filters[item].isIncludeUncheckedItems) {
                filterData[item] = filters[item];
            }
        }
        return filterData;
    }

    private _setTotalCount() {
        const model = this._gridApi.getModel();
        this.displayDataLen = this._dashboardTileService.setGridTotalCount(model);
        this.actionSubject$.next({ id: this.id, type: TamDashboardActions.SET_GRID_TOTAL_ITEMS, payload: this.displayDataLen });
    }

    private _updateGridRowItems(data) {
        const gridRowNodes = [];
        this._gridApi.forEachLeafNode((rowNode) => {
            gridRowNodes.push(rowNode);
        });
        const filtersData = this._gridApi.getFilterModel();
        const filters = this._filterDataValue(filtersData);
        this._dashboardTileService.updateGridRowItems(gridRowNodes, data).subscribe((result) => {
            // ag-grid v23.1.0 rename updateRowData to applyTransaction
            this._gridApi.updateRowData(result);
            const filterOldValue = new Map();
            for (const filterItem in filters) {
                if (filters.hasOwnProperty(filterItem) && filters[filterItem].values) {
                    const agGridFilter = this._gridApi.getFilterInstance(filterItem);
                    if (agGridFilter) {
                        const athleteFilterComponent = agGridFilter;
                        if (athleteFilterComponent) {
                            const allData = athleteFilterComponent.getDisplayedValues();
                            filterOldValue.set(filterItem, allData);
                        }
                    }
                }
            }
            setTimeout(() => {
                for (const filterItem in filters) {
                    if (filters.hasOwnProperty(filterItem)) {
                        const agGridFilter = this._gridApi.getFilterInstance(filterItem);
                        if (agGridFilter) {
                            const athleteFilterComponent = agGridFilter;
                            if (athleteFilterComponent) {
                                athleteFilterComponent.onRefreshFilter();
                                // tslint:disable-next-line: no-shadowed-variable
                                let data;
                                if (filters[filterItem].isNotBlank) {
                                    const allData = athleteFilterComponent.getDisplayedValues();
                                    data = allData.filter(item => (!!item && item !== BLANK_VALUE) || item === 0 || item === false);
                                } else {
                                    data = filterOldValue.get(filterItem);
                                }
                                athleteFilterComponent.selectValue(data);
                            }
                        }
                    }
                }
                this._gridApi.onFilterChanged();
                this._setTotalCount();
            }, 500);
        });
    }

    /**
     * Grid - set column visible, resize column width, pinned column, drag column will change columns
     */
    private _updateGroupRowByColumns(column?) {
        // set column visible, pinned column, drag column, redraw rows for indented rows
        if (column && (column.left === 0 || column.oldLeft === 0 || !column.visible)) {
            this.actionSubject$.next({
                id: this.id,
                type: TamDashboardActions.REDRAW_GRID_ROWS
            });
        }
        this.groupRowRendererParams.action$.next({
            type: TamDashboardCustomComponentsActions.UPDATE_COLUMN_WIDTH_BY_OPERATION
        });
    }
}
